import { Component, Input, Output, EventEmitter, ContentChildren, QueryList, TemplateRef, AfterContentInit, ChangeDetectionStrategy, ContentChild } from "@angular/core";
import { InlineColumnTemplateDirective } from "./directives/inline-column-template.directive";
import { InlineRowTemplateDirective } from "./directives/inline-row-template.directive";
import { GridSchema } from "./models/grid-schema.interface";
import { PageSizeConfig } from "./models/page-size-config.interface";
import { Paging } from "./models/paging.interface";
import { SortDirection } from "./models/sort-direction.enum";
import { Sorting } from "./models/sorting.interface";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { TelenetIconComponent } from "../telenet-icons/telenet-icon.component";
import { PageSizeComponent } from "./page-size/page-size.component";
import { TranslatePipe } from "@ngx-translate/core";
import { ColumnTemplateRefPipe } from "./pipes/column-template-ref.pipe";
import { RowDataPipe } from "./pipes/row-data.pipe";

@Component({
   selector: "my-cloud-grid",
   templateUrl: "./grid.component.html",
   styleUrls: ["./grid.component.scss"],
   changeDetection: ChangeDetectionStrategy.OnPush,
   imports: [
      NgClass,
      FaIconComponent,
      NgTemplateOutlet,
      TelenetIconComponent,
      PageSizeComponent,
      TranslatePipe,
      ColumnTemplateRefPipe,
      RowDataPipe
   ]
})
export class GridComponent<T> implements AfterContentInit {
   @Input() public schema!: GridSchema<T>;

   @Input() public data: Array<T> = [];

   @Input() public sorting: Sorting<T> = {} as Sorting<T>;
   @Input() public pageSize: number = 15;
   @Input() public paging: Paging = {} as Paging;
   @Input() public pageSizeConfig: PageSizeConfig = { min: 5, max: 100 };

   @Input() public selectable?: "single" | "multiple" | null;
   @Input() public simpleGridColumnTemplates?: QueryList<
      InlineColumnTemplateDirective
   >;
   @Input() public simpleGridRowTemplate?: InlineRowTemplateDirective;

   @Output() private pageChange = new EventEmitter<number>();
   @Output() private pageSizeChange = new EventEmitter<number>();
   @Output() private sortingChange = new EventEmitter<Sorting<T>>();
   @Output() private selectionChange = new EventEmitter<T | Array<T>>();
   @Output() private refresh = new EventEmitter<void>();
   @Output() private rowClick = new EventEmitter<T>();

   public selectedRows: Array<T> = [];
   public allSelected = false;

   public readonly SortDirection = SortDirection;

   public columnTemplateMap: Map<string, TemplateRef<any>> = new Map<
      string,
      TemplateRef<any>
   >();

   @ContentChildren(InlineColumnTemplateDirective, { descendants: true })
   private columnTemplates: QueryList<
      InlineColumnTemplateDirective
   > = {} as QueryList<InlineColumnTemplateDirective>;
   @ContentChild(InlineRowTemplateDirective)
   private rowTemplate: InlineRowTemplateDirective = {} as InlineRowTemplateDirective;

   public ngAfterContentInit(): void {
      for (const template of this.simpleGridColumnTemplates ||
         this.columnTemplates.toArray()) {
         this.columnTemplateMap.set(
            template.inlineColumnTemplate,
            template.templateRef
         );
      }
   }

   public changePage(requestedPageNumber: number): void {
      this.allSelected = false;
      this.selectedRows = [];

      this.pageChange.emit(requestedPageNumber);
   }

   public changePageSize(pageSize: number): void {
      this.pageSizeChange.emit(pageSize);
   }

   public changeSorting(field: keyof T, direction: SortDirection): void {
      this.allSelected = false;
      this.selectedRows = [];

      this.sortingChange.emit({ sortBy: field, sortDirection: direction });
   }

   public changeSelection(rowData: T): void {
      if (this.selectable === "multiple") {
         this.selectedRows = this.selectedRows.includes(rowData)
            ? this.selectedRows.filter(row => row !== rowData)
            : [...this.selectedRows, rowData];

         this.allSelected =
            this.selectedRows.length > 0 &&
            this.selectedRows.length === this.data.length;
         this.selectionChange.emit(this.selectedRows);
      }

      if (this.selectable === "single") {
         this.selectionChange.emit(rowData);
      }
   }

   public selectAll(): void {
      this.allSelected = !this.allSelected;

      this.selectedRows = this.allSelected ? [...this.data] : [];
      this.selectionChange.emit(this.selectedRows);
   }

   public onRowClicked(rowData: T, field: keyof T): void {
      if (
         this.schema.clickableRows &&
         !this.columnTemplateMap.get(field.toString())
      ) {
         this.rowClick.emit(rowData);
      }
   }

   public refreshIt(): void {
      this.refresh.emit();
   }

   public get rowTemplateRef(): TemplateRef<any> {
      return this.rowTemplate?.templateRef;
   }

   public get pages(): Array<number> {
      if (!this.paging) {
         return [];
      }

      const totalPages = this.paging.totalPages;
      const currentPage = this.paging.currentPage;

      if (totalPages < 5) {
         return this.generateRange(1, totalPages);
      }

      let pages: Array<number> = new Array<number>();
      if (currentPage < 5) {
         pages = this.generateRange(1, 5);
      }
      if (currentPage >= 5 && currentPage <= totalPages - 3) {
         pages = this.generateRange(currentPage - 2, currentPage + 2);
      }
      if (currentPage >= totalPages - 3) {
         pages = this.generateRange(totalPages - 4, totalPages);
      }

      return pages;
   }

   private generateRange(start: number, end: number): Array<number> {
      const step = 1; // generates array from start to end with end number included. (step 1)
      return Array.from(
         Array.from(Array(Math.ceil((end - start + 1) / step)).keys()),
         x => start + x * step
      );
   }
}
